import { useEffect, useState } from "react";
import { formatPrice } from "../utils/formatUtils";
import { usePortfolio } from '../context/PortfolioProvider';
import { useSocket } from '../context/SocketProvider';
import api from "../api/api";
import UpIcon from "../assets/green-tri.svg";
import DownIcon from "../assets/red-tri.svg";

export default function Portfolio() {
  const [holdings, setHoldings] = useState([]);
  const { portfolio } = usePortfolio();
  const socket = useSocket();

  useEffect(() => {
    async function fetchHoldings() {
      try {
        const response = await api.get("/user/holdings", { withCredentials: true });
        setHoldings(response.data);
      } catch (err) {
        console.error("Failed to fetch holdings:", err);
      }
    }
    fetchHoldings();
    if (!socket) return;
    const handleCoinDataUpdated = () => {
      fetchHoldings();
    };
    socket.on('coinDataUpdated', handleCoinDataUpdated);
    return () => {
      socket.off('coinDataUpdated', handleCoinDataUpdated);
    };
  }, [socket]);

  const hasHoldings = holdings.length > 0;

  return (
    <div className="container mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4">My Portfolio</h2>

      {hasHoldings ? (
        <>
        { portfolio && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6 text-sm">
            <div className="bg-stone-100 p-4 rounded shadow">
              <div className="text-stone-600">Portfolio Value</div>
              <div className="text-xl font-bold">${formatPrice(portfolio.total_portfolio_value)}</div>
            </div>
            <div className="bg-stone-100 p-4 rounded shadow">
              <div className="text-stone-600">P/L</div>
              <div className={`flex items-center text-xl font-bold ${portfolio.total_profit_loss >= 0 ? "text-green-500" : "text-red-500"}`}>
                <img src={portfolio.total_profit_loss >= 0 ? UpIcon : DownIcon} alt="change indicator" className="w-4 h-4 mr-1" />
                  <p className={`${portfolio.total_profit_loss >= 0 ? "+" : "-"}`}>${formatPrice(Math.abs(portfolio.total_profit_loss))} ({portfolio.total_return_percentage.toFixed(2)}%)</p>
              </div>
            </div>
            <div className="bg-stone-100 p-4 rounded shadow">
              <div className="text-stone-600">Available Balance</div>
              <div className="text-xl font-bold">${formatPrice(portfolio.available_balance)}</div>
            </div>
          </div>)
        }
          {/* Desktop View */}
          <div className="hidden lg:inline">
            <table className="w-full">
              <thead className="bg-blue-100 text-stone-600 text-sm text-left font-semibold">
                <tr>
                  <th className="p-4">Coin</th>
                  <th className="p-4">Amount</th>
                  <th className="p-4">Average Cost</th>
                  <th className="p-4">Current Price</th>
                  <th className="p-4">Market Value</th>
                  <th className="p-4">Total Return</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {holdings.map((holding) => {
                  return (
                    <tr key={holding.id} className="border-t hover:bg-stone-100">
                      <td className="p-4 font-semibold uppercase">{holding.symbol}</td>
                      <td className="p-4">{Number(holding.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })}</td>
                      <td className="p-4">${formatPrice(holding.average_price)}</td>
                      <td className="p-4">${formatPrice(holding.current_price)}</td>
                      <td className="p-4 font-semibold">${formatPrice(holding.market_value)}</td>
                      <td className={`p-4 font-semibold ${holding.profit_loss >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {holding.profit_loss >= 0 ? "+" : "-"}${Math.abs(holding.profit_loss).toFixed(2)}  <span className="font-normal">({(Number(holding.profit_loss_percentage) || 0).toFixed(2)}%)</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="lg:hidden space-y-4 text-sm">
            {holdings.map((holding) => {
              return (
                <div key={holding.id} className="border rounded-lg p-4">
                  <div className="flex justify-between font-bold">
                    <span className="uppercase">{holding.name}</span>
                    <span>{Number(holding.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 6 })} Coin{holding.quantity !== 1 && 's'}</span>
                  </div>
                  <p className="mb-3"><span className="uppercase">{holding.symbol}</span> Price: ${formatPrice(holding.current_price)}</p>

                  <hr/>

                    <div className="flex justify-between mt-3">
                      <span>Average Cost: </span>
                      <span>${formatPrice(holding.average_price)}</span>
                    </div>
                    <div className="flex justify-between mb-3">
                      <span>Market Value: </span>
                      <span>${formatPrice(holding.market_value)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Return:</span>
                      <span className={`font-bold ${holding.profit_loss >= 0 ? "text-green-500" : "text-red-500"}`}>
                        {holding.profit_loss >= 0 ? "+" : "-"}${Math.abs(holding.profit_loss).toFixed(2)} <span className="font-normal">({(Number(holding.profit_loss_percentage) || 0).toFixed(2)}%)</span>
                      </span>
                    </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="text-center text-gray-500 text-sm mt-8">
          No current positions.
        </div>
      )}
    </div>
  );
}